import { cn, Spinner, Stack } from 'component-library';
import { TFunction } from 'i18next';
import { FC, HTMLAttributes, MutableRefObject, ReactNode } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { MapRef, Popup } from 'react-map-gl';

import { LandscapeType, MAP_LAYER_FACTS } from '@/api/rest/resources/types/fact';
import { LANDLER_CAT_TO_OSM_CATEGORY } from '@/api/rest/resources/types/geoserver';
import { ProtectedAreasIbat, ProtectedAreasOsm } from '@/api/rest/resources/types/protectedAreas';
import { SuspenseBoundary } from '@/components';
import { Card } from '@/components/widgets/o/Card/Card';
import { Logger } from '@/lib/logs/logger';
import { isNullish } from '@/utils/booleans';

import { useGetFeatureInfo, useGetFeatureInfoFromCombinedLayers } from './useGetFeatureInfo';
import { useIsSiemensOrganization } from './useIsSiemensOrganization';

const LAYERS_WITH_TOOLTIP: MAP_LAYER_FACTS[] = [
  MAP_LAYER_FACTS.combined_ibat_lulc_urls,
  MAP_LAYER_FACTS.iucn_protected_areas,
  MAP_LAYER_FACTS.osm_protected_areas,
  MAP_LAYER_FACTS.combined_osm_lulc,
  MAP_LAYER_FACTS.indicative_lands,
  MAP_LAYER_FACTS.indigenous_lands,
];
const TOOLTIP_FEATURE_BY_LAYER: Partial<Record<MAP_LAYER_FACTS, string[]>> = {
  [MAP_LAYER_FACTS.combined_ibat_lulc_urls]: ['iucn_cat_norm', 'name', 'GRAY_INDEX'],
  [MAP_LAYER_FACTS.iucn_protected_areas]: ['iucn_cat_norm', 'name'],
  [MAP_LAYER_FACTS.osm_protected_areas]: ['name', 'landler_cat'],
  [MAP_LAYER_FACTS.combined_osm_lulc]: ['name', 'landler_cat', 'GRAY_INDEX'],
  [MAP_LAYER_FACTS.indicative_lands]: ['name', 'category', 'form_rec'],
  [MAP_LAYER_FACTS.indigenous_lands]: ['identity', 'form_rec', 'category'],
};

const COMBINED_LAYERS = {
  [MAP_LAYER_FACTS.combined_ibat_lulc_urls]: [
    MAP_LAYER_FACTS.combined_ibat_lulc_urls,
    MAP_LAYER_FACTS.iucn_protected_areas,
  ],
  [MAP_LAYER_FACTS.combined_osm_lulc]: [MAP_LAYER_FACTS.osm_protected_areas],
};

export type LayerTooltipProps = {
  longitude: number;
  latitude: number;
  x: number;
  y: number;
  onClose: () => void;
  layer: string | MAP_LAYER_FACTS;
  mapRef: MutableRefObject<MapRef | null>;
  year: string;
};

export const LayerTooltip = (props: LayerTooltipProps) => {
  if (!LAYERS_WITH_TOOLTIP.includes(props.layer as MAP_LAYER_FACTS)) {
    return null;
  }

  return (
    <LayerTooltipWrapper {...props}>
      <LayerFeaturesFetcher {...props} />
    </LayerTooltipWrapper>
  );
};

const LayerTooltipWrapper = ({
  longitude,
  latitude,
  onClose,
  children,
}: Pick<LayerTooltipProps, 'longitude' | 'latitude' | 'onClose'> & { children: ReactNode }) => {
  const { t } = useTranslation();

  return (
    <Popup
      longitude={longitude}
      latitude={latitude}
      closeButton={false}
      closeOnClick={false}
      anchor='top'
      maxWidth='420px'
      offset={8}
      onClose={onClose}
    >
      <div className='rounded-xl bg-white-100 p-4'>
        <button
          onClick={onClose}
          className='absolute right-2 top-2 text-neutral-500 hover:text-neutral-700'
          aria-label='Close'
        >
          <i className='ri-close-line' />
        </button>
        <ErrorBoundary
          FallbackComponent={() => (
            <Card styles={{ root: 'rounded-3xl p-4' }}>
              <Stack spacing={4}>
                <span className='typography-body1 text-text-disabled'>{t('global.widget.error.message')}</span>
              </Stack>
            </Card>
          )}
          onError={(error, info) => {
            Logger.error(`Plot card rendering error: ${error} ${info}`);
          }}
        >
          <SuspenseBoundary
            fallback={
              <Stack centerMain>
                <Spinner size={10} />
              </Stack>
            }
          >
            {children}
          </SuspenseBoundary>
        </ErrorBoundary>
      </div>
    </Popup>
  );
};

const LayerFeaturesFetcher = (props: LayerTooltipProps) => {
  if (
    [MAP_LAYER_FACTS.combined_ibat_lulc_urls, MAP_LAYER_FACTS.combined_osm_lulc].includes(
      props.layer as MAP_LAYER_FACTS,
    )
  ) {
    return <LayerFeaturesFetcherCombinedLayer {...props} />;
  }

  return <LayerFeaturesFetcherDefault {...props} />;
};

const LayerFeaturesFetcherCombinedLayer = ({ x, y, layer, mapRef, year, ...props }: LayerTooltipProps) => {
  let layers = COMBINED_LAYERS[layer as keyof typeof COMBINED_LAYERS];
  const isSiemensOrganization = useIsSiemensOrganization();

  if (isSiemensOrganization) {
    // TODO: BIG HACK - MVP-7252 - siemens does not use iucn_protected_areas, it uses siemens_protected_areas instead, but we don't have tooltips for this layer. Remove when this logic is no longer needed
    layers = layers.filter((l) => l !== MAP_LAYER_FACTS.iucn_protected_areas);
  }

  const response = useGetFeatureInfoFromCombinedLayers({ layers, x, y, mapRef, year });

  const features = response
    .map((d) => d?.data?.features?.[0]?.properties)
    .filter(Boolean)
    .reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return <LayerTooltipBase features={features} layer={layer} {...props} />;
};

const LayerFeaturesFetcherDefault = ({ x, y, layer, mapRef, year, ...props }: LayerTooltipProps) => {
  const reponse = useGetFeatureInfo({ layer, x, y, mapRef, year }).data;
  const features = reponse?.features?.[0]?.properties;

  return <LayerTooltipBase features={features} layer={layer} {...props} />;
};

const LayerTooltipBase = ({
  features,
  layer,
}: Pick<LayerTooltipProps, 'layer'> & {
  features?: Record<string, string | number | null>;
}) => {
  const { t } = useTranslation();
  const intlKeys = TOOLTIP_FEATURE_BY_LAYER[layer as MAP_LAYER_FACTS] ?? [];

  return (
    <Stack spacing={6}>
      {features && Object.keys(features).length > 0 ? (
        intlKeys.map((key) => {
          const value = features[`${key}`];
          if (value === undefined || isNullish(value)) {
            return null;
          }

          return (
            <div key={key}>
              <InfoBox>
                <InfoTitle>
                  {t(`shared.projects.map.layerTooltip.${key}`, {
                    defaultValue: key,
                  })}
                </InfoTitle>
                <InfoText>{getValueText(key, value, t)}</InfoText>
              </InfoBox>
            </div>
          );
        })
      ) : (
        <InfoBox>
          <InfoTitle>{t('shared.projects.map.layerTooltip.noDataTitle')}</InfoTitle>
          <InfoText>{t('shared.projects.map.layerTooltip.noData')}</InfoText>
        </InfoBox>
      )}
    </Stack>
  );
};

const InfoBox = ({ children }: { children: ReactNode }) => <Stack className='min-w-0 gap-2'>{children}</Stack>;

const InfoTitle: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, ...props }) => (
  <span className={cn('typography-overline block text-left text-text-disabled', className)} {...props} />
);

const InfoText: FC<HTMLAttributes<HTMLSpanElement>> = ({ className, ...props }) => (
  <span className={cn('typography-body1 block text-left', className)} {...props} />
);

const getValueText = (key: string, value: string | number, t: TFunction): string | number => {
  if (key === 'iucn_cat_norm') {
    return Object.values(ProtectedAreasIbat).includes(value as ProtectedAreasIbat) && typeof value === 'string'
      ? // eslint-disable-next-line sonarjs/no-nested-template-literals
        `${t(`global.protectedAreas.categories.${value}`, {
          defaultValue: value,
        })} - ${t(`global.protectedAreas.description.${value}`, {
          defaultValue: value,
        })}`
      : value;
  }

  if (key === 'landler_cat') {
    const protectedArea =
      typeof value === 'number'
        ? (LANDLER_CAT_TO_OSM_CATEGORY?.[value as keyof typeof LANDLER_CAT_TO_OSM_CATEGORY] as
            | ProtectedAreasOsm
            | undefined)
        : undefined;

    const protectedAreaKey = Object.entries(ProtectedAreasOsm).find(([, v]) => v === protectedArea)?.[0];

    return t(`global.protectedAreas.description.${protectedAreaKey}`, {
      defaultValue: String(value),
    });
  }

  if (key === 'GRAY_INDEX') {
    const grayIndexMap = {
      1: LandscapeType.tree_cover,
      2: LandscapeType.built_up,
      3: LandscapeType.cropland,
      4: LandscapeType.grassland,
      5: LandscapeType.bareground,
      6: LandscapeType.herbaceous_wetland,
      7: LandscapeType.shrubland,
      8: LandscapeType.permanent_water_bodies,
    };

    if (value === 0) {
      return t('shared.projects.map.layerTooltip.noDataTitle');
    }

    const landType =
      typeof value === 'number'
        ? (grayIndexMap?.[value as keyof typeof grayIndexMap] as LandscapeType | undefined)
        : undefined;

    return landType
      ? t(`global.landscapeTypes.${landType}`, {
          defaultValue: landType,
        })
      : value;
  }

  return value;
};
